import { useMemo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useChatSummaryMonthCost } from "@/hooks/use-chat-summary-costs";
import { monthOptions } from "@/lib/crm-cost-calc";
import { formatNOK } from "@/lib/format";
import { TONE_TEXT } from "@/lib/widget-status";

/** Kostnad for AI-oppsummering i Chat per måned (VIB-3027). Admin, under Drift → Systemhelse. */
export function ChatSummaryCostCard() {
  const months = useMemo(() => monthOptions(new Date()), []);
  const [month, setMonth] = useState(months[0].value);
  const { data, isLoading, error } = useChatSummaryMonthCost(month);

  return (
    <section aria-label="Kostnad for AI-oppsummering i Chat" className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">AI-oppsummering i Chat</h2>
          <p className="text-sm text-muted-foreground">
            Claude-kall fra «Oppsummer uleste». Lagret kostnad per kall. Ingen meldingstekst lagres.
          </p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-48" aria-label="Velg måned">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error ? (
        <p className={`text-sm ${TONE_TEXT.miss}`}>Kunne ikke hente kostnaden: {(error as Error).message}</p>
      ) : isLoading || !data ? (
        <Skeleton className="h-20" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="kpi-card">
            <div className="label">Kostnad</div>
            <div className="value">{formatNOK(data.cost_nok)}</div>
          </div>
          <div className="kpi-card">
            <div className="label">Oppsummeringer</div>
            <div className="value">{data.runs}</div>
            {data.failed_runs > 0 && <div className="delta">{data.failed_runs} feilet</div>}
          </div>
          <div className="kpi-card">
            <div className="label">Brukere</div>
            <div className="value">{data.users}</div>
          </div>
          <div className="kpi-card">
            <div className="label">Tokens inn / ut</div>
            <div className="value">
              {data.input_tokens.toLocaleString("nb-NO")} / {data.output_tokens.toLocaleString("nb-NO")}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
